import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import {
  Users,
  BookOpen,
  Calendar,
  CheckCircle,
  CreditCard,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Briefcase,
  Baby,
  AlertTriangle,

  ArrowRight,
  Clock,
  UserCheck,
  Mail
} from 'lucide-react';

const StatCard = ({ title, value, change, changeType, icon: Icon }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-purple-100 card-hover">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        {change && (
          <div className={`flex items-center mt-2 text-sm ${
            changeType === 'positive' ? 'text-green-600' : 'text-red-600'
          }`}>
            {changeType === 'positive' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span className="ml-1">{change}</span>
          </div>
        )}
      </div>
      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
        <Icon className="text-purple-600" size={24} />
      </div>
    </div>
  </div>
);

const QuickAction = ({ title, description, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className="w-full bg-white rounded-xl p-4 shadow-sm border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all text-left"
  >
    <div className="flex items-start space-x-4">
      <div className="w-10 h-10 bg-fuchsia-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="text-fuchsia-600" size={20} />
      </div>
      <div>
        <h4 className="font-semibold text-gray-900">{title}</h4>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
    </div>
  </button>
);

const AlertCard = ({ title, count, type, icon: Icon, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-white rounded-xl p-4 shadow-sm border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
  >
    <div className="flex items-center space-x-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        type === 'warning' ? 'bg-amber-100' : 
        type === 'danger' ? 'bg-red-100' : 
        type === 'info' ? 'bg-blue-100' : 'bg-purple-100'
      }`}>
        <Icon className={`${
          type === 'warning' ? 'text-amber-600' : 
          type === 'danger' ? 'text-red-600' : 
          type === 'info' ? 'text-blue-600' : 'text-purple-600'
        }`} size={20} />
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-gray-900">{title}</h4>
        <p className="text-sm text-gray-500 mt-1">{count} items require attention</p>
      </div>
      <ArrowRight size={18} className="text-gray-400" />
    </div>
  </div>
);

const AdminDashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  
  // Data states
  const [employees, setEmployees] = useState([]);
  const [parents, setParents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [classes, setClasses] = useState([]);

  const currentTenantId = user?.tenantId;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentTenantId) return;
      
      setIsLoading(true);
      try {
        // Fetch employees
        const { data: employeesData } = await supabase
          .from('users')
          .select('*')
          .eq('tenant_id', currentTenantId)
          .eq('role', 'employee');
        
        setEmployees(employeesData || []);
        
        // Fetch parents
        const { data: parentsData } = await supabase
          .from('users')
          .select('*')
          .eq('tenant_id', currentTenantId)
          .eq('role', 'parent');
        
        setParents(parentsData || []);
        
        // Fetch payments
        const { data: paymentsData } = await supabase
          .from('payments')
          .select('*')
          .eq('tenant_id', currentTenantId);
        
        setPayments(paymentsData || []);
        
        // Fetch communications
        const { data: commsData } = await supabase
          .from('communications')
          .select('*')
          .eq('tenant_id', currentTenantId)
          .order('created_at', { ascending: false })
          .limit(10);
        
        setCommunications(commsData || []);
        
        // Fetch enrollments
        const { data: enrollmentsData } = await supabase
          .from('enrollments')
          .select('*')
          .eq('tenant_id', currentTenantId);
        
        setEnrollments(enrollmentsData || []);
        
        // Fetch classes
        const { data: classesData } = await supabase
          .from('classes')
          .select('*')
          .eq('tenant_id', currentTenantId);
        
        setClasses(classesData || []);
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [currentTenantId]);

  // Calculate stats
  const overduePayments = payments.filter(p => p.status === 'overdue' || p.status === 'pending');
  const parentsWithOverdue = [...new Set(overduePayments.map(p => p.parent_id))].length;
  
  // Recent signups (within last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const recentParentSignups = parents.filter(p => new Date(p.created_at) > thirtyDaysAgo).length;
  const recentEmployeeSignups = employees.filter(e => new Date(e.created_at) > thirtyDaysAgo).length;
  
  // Low enrollment classes (less than 50% capacity)
  const lowEnrollmentClasses = classes.filter(cls => {
    const enrollmentCount = enrollments.filter(e => e.class_id === cls.id && e.status === 'active').length;
    return enrollmentCount < (cls.max_capacity * 0.5);
  });

  // Recent employee logins (sort by last_login)
  const recentEmployeeLogins = employees
    .filter(e => e.last_login)
    .sort((a, b) => new Date(b.last_login) - new Date(a.last_login))
    .slice(0, 5);

  // Recent parent activity
  const recentParentActivity = parents
    .filter(p => p.last_login)
    .sort((a, b) => new Date(b.last_login) - new Date(a.last_login))
    .slice(0, 5);

  const stats = [
    { title: 'Total Students', value: '156', change: '+12 this month', changeType: 'positive', icon: Users },
    { title: 'Active Classes', value: '24', change: '+3 this month', changeType: 'positive', icon: BookOpen },
    { title: 'Enrollments', value: '203', change: '98% active', changeType: 'positive', icon: Calendar },
    { title: 'Monthly Revenue', value: '$12,450', change: '+8.5% vs last month', changeType: 'positive', icon: DollarSign },
    { title: 'Total Employees', value: employees.length.toString(), change: recentEmployeeSignups > 0 ? `+${recentEmployeeSignups} new` : null, changeType: 'positive', icon: Briefcase },
    { title: 'Total Parents', value: parents.length.toString(), change: recentParentSignups > 0 ? `+${recentParentSignups} new` : null, changeType: 'positive', icon: Baby },
  ];

  const quickActions = [
    { title: 'Add New Student', description: 'Enroll a new student in your programs', icon: Users },
    { title: 'Create Class', description: 'Set up a new class or program', icon: BookOpen },
    { title: 'Take Attendance', description: 'Record attendance for today\'s classes', icon: CheckCircle },
    { title: 'Send Message', description: 'Email or text parents and staff', icon: CreditCard },
  ];

  const recentActivity = [
    { action: 'New enrollment', detail: 'Emma Johnson enrolled in Ballet Beginners', time: '2 hours ago' },
    { action: 'Payment received', detail: '$120 from Michael Smith', time: '3 hours ago' },
    { action: 'Attendance marked', detail: 'Ballet Beginners - 14 present, 1 absent', time: '5 hours ago' },
    { action: 'New student added', detail: 'Liam Johnson was added to the system', time: '1 day ago' },
  ];

  const alerts = [
    { 
      title: 'Overdue Payments', 
      count: overduePayments.length, 
      type: 'danger', 
      icon: CreditCard,
      onClick: () => navigate('/admin/payments')
    },
    { 
      title: 'Low Enrollment Classes', 
      count: lowEnrollmentClasses.length, 
      type: 'warning', 
      icon: Users,
      onClick: () => navigate('/admin/classes')
    },
    { 
      title: 'Pending Approvals', 
      count: 0, 
      type: 'info', 
      icon: CheckCircle,
      onClick: () => navigate('/admin/enrollments')
    },
    { 
      title: 'System Notifications', 
      count: 2, 
      type: 'info', 
      icon: AlertTriangle,
      onClick: () => navigate('/admin/settings')
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-gray-500 mt-1">
          Here's what's happening at your school today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Alerts/Issues Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Alerts & Issues</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {alerts.map((alert, index) => (
            <AlertCard key={index} {...alert} />
          ))}
        </div>
      </div>

      {/* Quick Actions & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {quickActions.map((action, index) => (
              <QuickAction key={index} {...action} />
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="bg-white rounded-xl shadow-sm border border-purple-100">
            {recentActivity.map((activity, index) => (
              <div
                key={index}
                className={`p-4 ${index !== recentActivity.length - 1 ? 'border-b border-purple-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{activity.action}</p>
                    <p className="text-sm text-gray-500 mt-1">{activity.detail}</p>
                  </div>
                  <span className="text-xs text-gray-400">{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Overview & Parents Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Team Overview Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Team Overview</h2>
            <button 
              onClick={() => navigate('/admin/settings')}
              className="text-sm text-purple-600 hover:text-purple-700 flex items-center"
            >
              Manage Team <ArrowRight size={16} className="ml-1" />
            </button>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
            {/* Employee Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <Briefcase className="text-purple-600 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
                <p className="text-sm text-gray-500">Total Employees</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <UserCheck className="text-green-600 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-gray-900">
                  {employees.filter(e => e.status === 'active').length}
                </p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
              <div className="text-center p-4 bg-fuchsia-50 rounded-lg">
                <TrendingUp className="text-fuchsia-600 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-gray-900">{recentEmployeeSignups}</p>
                <p className="text-sm text-gray-500">New (30 days)</p>
              </div>
            </div>

            {/* Recent Employee Logins */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Employee Activity</h3>
              {recentEmployeeLogins.length > 0 ? (
                <div className="space-y-3">
                  {recentEmployeeLogins.map((employee, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <Briefcase size={14} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{employee.first_name} {employee.last_name}</p>
                          <p className="text-xs text-gray-500">{employee.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock size={12} className="mr-1" />
                        {new Date(employee.last_login).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No recent employee activity</p>
              )}
            </div>
          </div>
        </div>

        {/* Parents Overview Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Parents Overview</h2>
            <button 
              onClick={() => navigate('/admin/communications')}
              className="text-sm text-purple-600 hover:text-purple-700 flex items-center"
            >
              View Communications <ArrowRight size={16} className="ml-1" />
            </button>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-6">
            {/* Parent Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <Baby className="text-purple-600 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-gray-900">{parents.length}</p>
                <p className="text-sm text-gray-500">Total Parents</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <CreditCard className="text-red-600 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-gray-900">{parentsWithOverdue}</p>
                <p className="text-sm text-gray-500">Overdue</p>
              </div>
              <div className="text-center p-4 bg-fuchsia-50 rounded-lg">
                <TrendingUp className="text-fuchsia-600 mx-auto mb-2" size={24} />
                <p className="text-2xl font-bold text-gray-900">{recentParentSignups}</p>
                <p className="text-sm text-gray-500">New (30 days)</p>
              </div>
            </div>

            {/* Recent Parent Activity */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Parent Activity</h3>
              {recentParentActivity.length > 0 ? (
                <div className="space-y-3">
                  {recentParentActivity.map((parent, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-fuchsia-100 rounded-full flex items-center justify-center">
                          <Baby size={14} className="text-fuchsia-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{parent.first_name} {parent.last_name}</p>
                          <p className="text-xs text-gray-500">{parent.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock size={12} className="mr-1" />
                        {new Date(parent.last_login).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No recent parent activity</p>
              )}
            </div>

            {/* Recent Communications */}
            {communications.length > 0 && (
              <div className="mt-6 pt-6 border-t border-purple-100">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Communications</h3>
                <div className="space-y-2">
                  {communications.slice(0, 3).map((comm, index) => (
                    <div key={index} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg">
                      <Mail size={16} className="text-purple-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{comm.subject}</p>
                        <p className="text-xs text-gray-500">{comm.type} • {new Date(comm.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming Classes */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Today's Classes</h2>
        <div className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden">
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[600px]">
            <thead className="bg-purple-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Class</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Instructor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Enrolled</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-purple-700 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-purple-50">
              <tr>
                <td className="px-6 py-4 font-medium text-gray-900">Ballet Beginners</td>
                <td className="px-6 py-4 text-gray-500">4:00 PM - 5:00 PM</td>
                <td className="px-6 py-4 text-gray-500">Demo Teacher</td>
                <td className="px-6 py-4 text-gray-500">14/15</td>
                <td className="px-6 py-4">
                  <span className="badge badge-success">Active</span>
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 font-medium text-gray-900">Gymnastics Level 1</td>
                <td className="px-6 py-4 text-gray-500">3:30 PM - 4:30 PM</td>
                <td className="px-6 py-4 text-gray-500">Demo Teacher</td>
                <td className="px-6 py-4 text-gray-500">10/12</td>
                <td className="px-6 py-4">
                  <span className="badge badge-success">Active</span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
